/**
 * Pattern builder — continue a pattern, build one, or name the rule.
 *
 * The unit is expressed as element slots: AB is [0,1], AAB is [0,0,1], AABB is
 * [0,0,1,1]. A custom unit is any short list of slots, which is how "she
 * designs the pattern" works without inventing a new schema.
 *
 * Modes run easiest to hardest: identify (name the rule) → continue (extend it)
 * → create (make one and say what it is).
 */

import { z } from 'zod'
import { ColorTokenSchema, EmojiSchema, IdSchema, LabelSchema, isUnique, specFor } from '../common'

export const PATTERN_TYPES = ['AB', 'AAB', 'ABC', 'AABB', 'custom'] as const
export type PatternType = (typeof PATTERN_TYPES)[number]

export const PATTERN_MODES = ['continue', 'create', 'identify'] as const

export const BUILT_IN_PATTERN_UNITS: Record<Exclude<PatternType, 'custom'>, readonly number[]> = {
  AB: [0, 1],
  AAB: [0, 0, 1],
  ABC: [0, 1, 2],
  AABB: [0, 0, 1, 1],
}

export const PatternElementSchema = z.object({
  id: IdSchema,
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  colorToken: ColorTokenSchema.optional(),
})

export const PatternBuilderConfigSchema = z
  .object({
    patternType: z.enum(PATTERN_TYPES),
    /** Slot indexes into `elements`. Required when patternType is "custom". */
    customUnit: z.array(z.number().int().min(0).max(5)).min(2).max(6).optional(),
    elements: z.array(PatternElementSchema).min(2).max(6),
    /** Total items in the finished strip. */
    sequenceLength: z.number().int().min(4).max(20),
    mode: z.enum(PATTERN_MODES),
    /** How many items are already laid out in "continue" mode. */
    revealCount: z.number().int().min(1).max(19).optional(),
    /** Answer tiles offered in "identify" mode. */
    choiceCount: z.number().int().min(2).max(4).default(3),
    /** She says the pattern out loud as she builds — "red, blue, red, blue". */
    chantAloud: z.boolean().default(true),
  })
  .superRefine((config, ctx) => {
    if (!isUnique(config.elements.map((element) => element.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: 'element ids must be unique' })
    }

    let unit: readonly number[]
    if (config.patternType === 'custom') {
      if (!config.customUnit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnit'],
          message: 'a custom pattern needs its unit spelled out, e.g. [0,1,1]',
        })
        return
      }
      unit = config.customUnit
      if (new Set(unit).size < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customUnit'],
          message: 'a pattern needs at least two different elements',
        })
      }
    } else {
      unit = BUILT_IN_PATTERN_UNITS[config.patternType]
    }

    const highest = Math.max(...unit)
    if (highest >= config.elements.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['elements'],
        message: `the unit uses element ${highest} but only ${config.elements.length} elements are given`,
      })
    }
    if (config.sequenceLength < unit.length * 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sequenceLength'],
        message: `show the unit at least twice: ${unit.length * 2} items minimum`,
      })
    }
    if (config.mode === 'continue' && config.revealCount !== undefined) {
      if (config.revealCount < unit.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['revealCount'],
          message: 'reveal at least one whole unit or there is no pattern to see',
        })
      }
      if (config.revealCount >= config.sequenceLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['revealCount'],
          message: 'leave at least one item for her to place',
        })
      }
    }
    if (config.mode === 'identify' && config.choiceCount > config.elements.length + 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['choiceCount'],
        message: 'more choices than there is material to build them from',
      })
    }
  })

export type PatternBuilderConfig = z.infer<typeof PatternBuilderConfigSchema>

export const PatternBuilderSpecSchema = specFor('pattern_builder', PatternBuilderConfigSchema)

/** The repeating unit for any pattern type. Shared by the renderer and the factory. */
export function patternUnit(patternType: PatternType, customUnit?: readonly number[]): readonly number[] {
  if (patternType === 'custom') return customUnit ?? []
  return BUILT_IN_PATTERN_UNITS[patternType]
}

/** The full expected strip, as element indexes. */
export function expandPattern(
  patternType: PatternType,
  sequenceLength: number,
  customUnit?: readonly number[],
): number[] {
  const unit = patternUnit(patternType, customUnit)
  if (unit.length === 0) return []
  return Array.from({ length: sequenceLength }, (_, index) => unit[index % unit.length])
}
